import { z } from "zod";
import { isNotFoundPreviewPath, NOT_FOUND_PREVIEW_PATH } from "./bundlePages";
import { canonicalJson, digest, hashBytes, hashText } from "./canonical";
const MAX_FILES = 500;
const MAX_FILE_BYTES = 5 * 1024 * 1024;
const MAX_BUNDLE_BYTES = 25 * 1024 * 1024;
const MAX_WORKSPACE_MANIFEST_BYTES = 256 * 1024;
export const DEFAULT_BUNDLE_PREVIEW_CSP = "default-src 'self'; script-src 'self' 'unsafe-inline'; style-src 'self' 'unsafe-inline' https://fonts.googleapis.com; img-src 'self' data: blob: https:; font-src 'self' data: https:; connect-src 'none'; worker-src 'none'; object-src 'none'; base-uri 'none'; form-action 'none'";
const identifier = z.string().regex(/^[A-Za-z][A-Za-z0-9_-]{0,63}$/);
const hexDigest = z.string().regex(/^[a-f0-9]{64}$/);
const contentType = z.string().min(1).max(200).refine((value) => !/[\r\n]/.test(value), "Invalid content type");
const bindingId = z.string().regex(/^[A-Za-z][A-Za-z0-9_-]{0,63}$/);
const recordField = z.string().regex(/^[A-Za-z][A-Za-z0-9_]{0,63}$/);
/** What a template binding or node names as its record: the member a repeat is showing. `@` can't start a real record id. */
export const TEMPLATE_RECORD_ID = "@item";
/** The current category/tag heading, supplied by the requested module route. */
export const GROUP_RECORD_ID = "@group";
/** The most cards one repeat shows. */
export const MAX_REPEAT_CARDS = 60;
const bindingRecordId = bindingId.or(z.literal(TEMPLATE_RECORD_ID)).or(z.literal(GROUP_RECORD_ID));
const BundleFileSchema = z.object({
    sha256: hexDigest,
    contentType,
    size: z.number().int().nonnegative().max(MAX_FILE_BYTES),
}).strict();
/** `product` marks a direct catalogue reference that disappears safely when hidden or deleted. */
const BundleBindingSchema = z.discriminatedUnion("kind", [
    z.object({ id: bindingId, file: z.string(), kind: z.literal("text"), recordId: bindingRecordId, field: recordField, repeat: bindingId.optional(), product: z.unknown().optional() }).strict(),
    z.object({ id: bindingId, file: z.string(), kind: z.literal("attribute"), recordId: bindingRecordId, field: recordField, attribute: z.enum(["href", "src", "alt", "title", "aria-label"]), repeat: bindingId.optional(), product: z.unknown().optional() }).strict(),
    z.object({ id: bindingId, file: z.string(), kind: z.literal("image"), recordId: bindingRecordId, field: recordField.optional(), repeat: bindingId.optional(), product: z.unknown().optional() }).strict(),
    z.object({ id: bindingId, file: z.string(), kind: z.literal("markdown"), recordId: bindingRecordId, field: z.enum(["body", "description"]), repeat: bindingId.optional(), product: z.unknown().optional() }).strict(),
]);
const BundleNodeSchema = z.object({
    id: bindingId,
    file: z.string(),
    type: z.enum(["header", "footer", "nav", "section", "heading", "text", "image", "link", "repeat", "card", "control"]),
    recordIds: z.array(bindingRecordId).max(32).default([]),
    /** Set on a node inside a repeat's template: each card gets its own copy, `<id>~<member id>`. */
    repeat: bindingId.optional(),
}).strict();
/** One list the design writes once: the container marked `data-stackje-repeat="<id>"` shows its one template child per member of `collection`. */
const BundleRepeatSchema = z.object({
    id: bindingId,
    file: z.string(),
    collection: bindingId,
    start: z.number().int().min(0).optional(),
    limit: z.number().int().min(1).max(MAX_REPEAT_CARDS).optional(),
    paginate: z.literal(true).optional(),
}).strict();
export const BundleManifestSchema = z.object({
    schemaVersion: z.literal(1),
    rendererVersion: z.literal("bundle-v1"),
    siteId: identifier,
    digest: hexDigest,
    files: z.record(BundleFileSchema),
    routes: z.record(z.string()),
    bindings: z.array(BundleBindingSchema).max(2_000),
    nodes: z.array(BundleNodeSchema).max(2_000),
    repeats: z.array(BundleRepeatSchema).max(200).optional(),
    itemPages: z.record(bindingId).optional(),
}).strict();
/**
 * The page paths each record is shown on: through a binding, or as one of a node's records. A
 * record missing from the map isn't placed by this design.
 */
export function recordPlacements(manifest, content) {
    const routesByFile = new Map();
    for (const [path, file] of Object.entries(manifest.routes))
        routesByFile.set(file, [...(routesByFile.get(file) ?? []), path]);
    // The required error page can use content too; it still protects those records from deletion.
    routesByFile.set("404.html", [NOT_FOUND_PREVIEW_PATH]);
    const usage = new Map();
    const place = (recordId, file) => {
        const places = usage.get(recordId) ?? new Set();
        for (const path of routesByFile.get(file) ?? [])
            places.add(path);
        usage.set(recordId, places);
    };
    for (const binding of manifest.bindings) {
        // A repeat shows whichever members the collection holds, so it never keeps one of them from being deleted.
        if (binding.recordId === TEMPLATE_RECORD_ID || binding.recordId === GROUP_RECORD_ID)
            continue;
        place(binding.recordId, binding.file);
        // An item's photo is placed wherever its item is, including items a reorder swaps into the slot.
        if (content && binding.kind === "image" && binding.field === "image") {
            const shown = new Set([binding.recordId]);
            for (const collection of Object.values(content.collections))
                if (collection.designOrder?.includes(binding.recordId))
                    for (const id of collection.memberIds)
                        shown.add(id);
            for (const id of shown) {
                const record = content.recordsById[id];
                const photo = record && (record.kind === "service" || record.kind === "project" || record.kind === "item") ? record.fields.image : undefined;
                if (photo)
                    place(photo, binding.file);
            }
        }
    }
    for (const node of manifest.nodes)
        for (const recordId of node.recordIds)
            if (recordId !== TEMPLATE_RECORD_ID && recordId !== GROUP_RECORD_ID)
                place(recordId, node.file);
    return usage;
}
export const BundleDraftManifestSchema = z.object({
    schemaVersion: z.literal(1),
    siteId: identifier,
    draftId: identifier,
    parentDraftId: identifier.nullable(),
    sequence: z.number().int().nonnegative().max(1_000_000_000),
    contentVersionId: identifier,
    rendererVersion: z.literal("bundle-v1"),
    designDigest: hexDigest,
    contentDigest: hexDigest,
    brandDigest: hexDigest,
    presentationDigest: hexDigest,
    routes: z.record(z.string()),
    workspace: z.object({ provider: z.literal("sprites"), id: z.string().min(1).max(128), checkpointId: z.string().min(1).max(256).optional() }).strict(),
    canonicalOrigin: z.string().url().max(2_048).refine((value) => {
        const origin = new URL(value);
        return origin.protocol === "https:" && !origin.username && !origin.password && origin.pathname === "/" && !origin.search && !origin.hash;
    }, "Expected HTTPS origin"),
    digest: hexDigest,
}).strict();
/** The hand-written (or generated) `stackje.json` a design workspace carries: routes, bindings and nodes, with no file digests. */
export const WorkspaceBundleManifestSchema = z.object({
    schemaVersion: z.literal(1),
    rendererVersion: z.literal("bundle-v1"),
    routes: z.record(z.string()),
    bindings: z.array(BundleBindingSchema).max(2_000),
    nodes: z.array(BundleNodeSchema).max(2_000).default([]),
    repeats: z.array(BundleRepeatSchema).max(200).optional(),
    itemPages: z.record(bindingId).optional(),
}).strict().refine((value) => Object.keys(value.routes).length <= MAX_FILES, "Too many bundle routes").superRefine((value, context) => {
    const problem = repeatProblem(value);
    if (problem)
        context.addIssue({ code: z.ZodIssueCode.custom, message: problem });
});
function bytes(body) {
    return typeof body === "string" ? new TextEncoder().encode(body) : body;
}
function validateFilePath(path) {
    if (!path || path.length > 512 || path.startsWith("/") || path.endsWith("/") || path.includes("\\") || path.includes("\0")) {
        throw new Error(`Invalid bundle path: ${path}`);
    }
    const parts = path.split("/");
    if (parts.some((part) => !part || part === "." || part === ".."))
        throw new Error(`Invalid bundle path: ${path}`);
    return path;
}
function validateRoute(route) {
    if (!route.startsWith("/") || route.length > 2_048 || route.includes("?") || route.includes("#") || route.includes("\\") || route.includes("\0")) {
        throw new Error(`Invalid bundle route: ${route}`);
    }
    let decoded;
    try {
        decoded = decodeURIComponent(route);
    }
    catch {
        throw new Error(`Invalid bundle route: ${route}`);
    }
    if (decoded.split("/").some((part) => part === "." || part === ".."))
        throw new Error(`Invalid bundle route: ${route}`);
    if (isNotFoundPreviewPath(decoded))
        throw new Error(`Reserved bundle route: ${route}`);
    return route;
}
function prefix(siteId, artifactDigest) {
    return `bundles/${siteId}/${artifactDigest}`;
}
function inferredContentType(path) {
    const extension = path.split(".").pop()?.toLowerCase();
    const known = {
        html: "text/html; charset=utf-8", css: "text/css; charset=utf-8", js: "text/javascript; charset=utf-8",
        mjs: "text/javascript; charset=utf-8", json: "application/json", xml: "application/xml", txt: "text/plain; charset=utf-8",
        svg: "image/svg+xml", png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", webp: "image/webp", gif: "image/gif",
        ico: "image/x-icon", avif: "image/avif", woff: "font/woff", woff2: "font/woff2", ttf: "font/ttf", otf: "font/otf",
        wasm: "application/wasm", mp4: "video/mp4", webm: "video/webm", mp3: "audio/mpeg", webmanifest: "application/manifest+json",
    };
    return extension && known[extension] ? known[extension] : "application/octet-stream";
}
/** Decodes the few entities an attribute value commonly uses, to compare it with the DOM's value. */
export function decodeEntities(value) {
    const named = { amp: "&", quot: "\"", apos: "'", lt: "<", gt: ">", "#39": "'", "#34": "\"" };
    return value.replace(/&(amp|quot|apos|lt|gt|#39|#34);/g, (entity, name) => named[name] ?? entity);
}
/**
 * Why a manifest's repeats are unusable, or null. Each repeat has a unique id and names a routed page;
 * every `@item` binding or node names a repeat on its own page, and only `@item` bindings carry `repeat`.
 */
export function repeatProblem(manifest) {
    const repeats = new Map();
    const routed = new Set(Object.values(manifest.routes));
    for (const repeat of manifest.repeats ?? []) {
        if (repeats.has(repeat.id))
            return `Duplicate bundle repeat: ${repeat.id}`;
        if (!routed.has(repeat.file))
            return `Bundle repeat file is not a routed page: ${repeat.id}`;
        repeats.set(repeat.id, repeat);
    }
    const onItsPage = (item) => item.repeat === undefined || repeats.get(item.repeat)?.file === item.file;
    for (const binding of manifest.bindings) {
        if (binding.recordId === GROUP_RECORD_ID && (binding.kind !== "text" || binding.field !== "label" || binding.repeat !== undefined || binding.product === true || !Object.entries(manifest.routes).some(([path, file]) => file === binding.file && /\/(?:tag\/\{tag\}|c\/\{(?:slug|category)\})(?:\/page\/\{n\})?$/.test(path))))
            return `Group binding needs group.label on a category or tag page: ${binding.id}`;
        const template = binding.recordId === TEMPLATE_RECORD_ID;
        if (binding.kind === "markdown" && (binding.recordId !== TEMPLATE_RECORD_ID || binding.repeat !== undefined || !manifest.itemPages?.[binding.file]))
            return `Markdown binding belongs on an item page: ${binding.id}`;
        if (template && binding.repeat === undefined && !manifest.itemPages?.[binding.file])
            return `Template binding names no repeat: ${binding.id}`;
        if (!template && binding.repeat !== undefined)
            return `Only template bindings may name a repeat: ${binding.id}`;
        if (!onItsPage(binding))
            return `Binding repeat is not on its page: ${binding.id}`;
    }
    for (const node of manifest.nodes) {
        if (node.recordIds.includes(TEMPLATE_RECORD_ID) && node.repeat === undefined && !manifest.itemPages?.[node.file])
            return `Template node names no repeat: ${node.id}`;
        if (!onItsPage(node))
            return `Node repeat is not on its page: ${node.id}`;
    }
    return null;
}
function validateRepeatMarkers(repeats, bodies) {
    for (const repeat of repeats) {
        if (!new TextDecoder().decode(bodies.get(repeat.file)).includes(`data-stackje-repeat="${repeat.id}"`))
            throw new Error(`Bundle repeat marker is missing: ${repeat.id}`);
    }
}
function validateBindings(bindings, files, bodies) {
    // Report every repeated id at once: a shared header repeats several bindings on each page.
    const ids = new Set();
    const duplicates = new Set();
    for (const binding of bindings) {
        if (ids.has(binding.id))
            duplicates.add(binding.id);
        ids.add(binding.id);
    }
    if (duplicates.size)
        throw new Error(`Duplicate bundle binding: ${[...duplicates].join(", ")}`);
    for (const binding of bindings) {
        const path = validateFilePath(binding.file);
        const file = files[path];
        if (!file?.contentType.startsWith("text/html"))
            throw new Error(`Bundle binding file is missing or not HTML: ${path}`);
        const html = new TextDecoder().decode(bodies.get(path));
        const marker = `data-stackje-bind="${binding.id}"`;
        const first = html.indexOf(marker);
        if (first < 0 || html.indexOf(marker, first + marker.length) >= 0)
            throw new Error(`Bundle binding marker must occur exactly once: ${binding.id}`);
    }
}
function validateNodes(nodes, files, bodies) {
    const identities = new Set();
    for (const node of nodes) {
        const path = validateFilePath(node.file);
        const file = files[path];
        if (!file?.contentType.startsWith("text/html"))
            throw new Error(`Bundle node file is missing or not HTML: ${path}`);
        const identity = `${path}\0${node.id}`;
        if (identities.has(identity))
            throw new Error(`Duplicate bundle node: ${node.id}`);
        identities.add(identity);
        const html = new TextDecoder().decode(bodies.get(path));
        if (!html.includes(`data-stackje-node="${node.id}"`))
            throw new Error(`Bundle node marker is missing: ${node.id}`);
    }
}
async function verifiedPut(objects, key, body, expectedContentType, expectedHash) {
    await objects.put(key, body, expectedContentType);
    const saved = await objects.get(key);
    if (!saved || saved.contentType !== expectedContentType || await hashBytes(saved.body) !== expectedHash) {
        throw new Error("Bundle artifact verification failed");
    }
}
/** Stores raw static output once, addressed by a digest of its routes and exact file bytes. */
export class BundleArtifactService {
    objects;
    constructor(objects) {
        this.objects = objects;
    }
    async ingest(siteIdInput, source, options = {}) {
        const siteId = identifier.parse(siteIdInput);
        if (source.files.length === 0 || source.files.length > MAX_FILES)
            throw new Error("Bundle file count is out of range");
        const files = {};
        const bodies = new Map();
        let totalBytes = 0;
        for (const item of source.files) {
            const path = validateFilePath(item.path);
            if (Object.hasOwn(files, path))
                throw new Error(`Duplicate bundle path: ${path}`);
            const body = bytes(item.body);
            if (body.byteLength > MAX_FILE_BYTES)
                throw new Error(`Bundle file is too large: ${path}`);
            totalBytes += body.byteLength;
            if (totalBytes > MAX_BUNDLE_BYTES)
                throw new Error("Bundle is too large");
            const parsedContentType = contentType.parse(item.contentType);
            files[path] = { sha256: await hashBytes(body), contentType: parsedContentType, size: body.byteLength };
            bodies.set(path, body);
        }
        if (!Object.hasOwn(files, "index.html"))
            throw new Error("Bundle must contain index.html");
        if (!Object.hasOwn(files, "404.html"))
            throw new Error("Bundle must contain 404.html");
        const routes = {};
        for (const [routeInput, pathInput] of Object.entries(source.routes)) {
            const route = validateRoute(routeInput);
            const path = validateFilePath(pathInput);
            if (!Object.hasOwn(files, path))
                throw new Error(`Bundle route target is missing: ${path}`);
            if (!files[path].contentType.startsWith("text/html"))
                throw new Error(`Bundle route target is not HTML: ${path}`);
            routes[route] = path;
        }
        // A newly registered site has a complete inert artifact but no public routes until its first
        // design is built. Every non-blank bundle must still expose `/` from index.html.
        if (Object.keys(routes).length > 0 && routes["/"] !== "index.html")
            throw new Error("Bundle root route must target index.html");
        const bindings = BundleBindingSchema.array().max(2_000).parse(source.bindings);
        validateBindings(bindings, files, bodies);
        const nodes = BundleNodeSchema.array().max(2_000).parse(source.nodes ?? []);
        validateNodes(nodes, files, bodies);
        const repeats = BundleRepeatSchema.array().max(200).parse(source.repeats ?? []);
        const itemPages = source.itemPages ?? {};
        for (const [file, collection] of Object.entries(itemPages))
            if (!files[file]?.contentType.startsWith("text/html") || !identifier.safeParse(collection).success)
                throw new Error(`Invalid item page: ${file}`);
        const problem = repeatProblem({ routes, bindings, nodes, repeats, itemPages });
        if (problem)
            throw new Error(problem);
        validateRepeatMarkers(repeats, bodies);
        // Left out when empty, so a design without repeats keeps the digest it always had.
        const unsigned = { schemaVersion: 1, rendererVersion: "bundle-v1", siteId, files, routes, bindings, nodes, ...(repeats.length ? { repeats } : {}), ...(Object.keys(itemPages).length ? { itemPages } : {}) };
        const artifactDigest = await digest(unsigned);
        const manifest = { ...unsigned, digest: artifactDigest };
        options.validate?.(manifest);
        if (options.persist === false)
            return manifest;
        const artifactPrefix = prefix(siteId, artifactDigest);
        for (const [path, body] of bodies) {
            const file = files[path];
            await verifiedPut(this.objects, `${artifactPrefix}/files/${path}`, body, file.contentType, file.sha256);
        }
        const serialized = canonicalJson(manifest);
        await verifiedPut(this.objects, `${artifactPrefix}/manifest.json`, serialized, "application/json", await hashText(serialized));
        return manifest;
    }
    /** Accepts the conventional stackje.json plus output files directly from any workspace provider. */
    async ingestWorkspace(siteId, workspace, outputDirectory, options = {}) {
        const root = outputDirectory.replace(/\/$/, "");
        const entries = await workspace.listFiles(root);
        const manifestEntry = entries.find((entry) => entry.path === "stackje.json");
        if (!manifestEntry || manifestEntry.size > MAX_WORKSPACE_MANIFEST_BYTES)
            throw new Error("Workspace stackje.json is missing or too large");
        const publicEntries = entries.filter((entry) => entry.path !== "stackje.json");
        if (publicEntries.length === 0 || publicEntries.length > MAX_FILES)
            throw new Error("Bundle file count is out of range");
        let declaredBytes = 0;
        for (const entry of publicEntries) {
            validateFilePath(entry.path);
            if (entry.size > MAX_FILE_BYTES)
                throw new Error(`Bundle file is too large: ${entry.path}`);
            declaredBytes += entry.size;
            if (declaredBytes > MAX_BUNDLE_BYTES)
                throw new Error("Bundle is too large");
        }
        const manifestBytes = await workspace.readFile(`${root}/stackje.json`);
        if (manifestBytes.byteLength !== manifestEntry.size)
            throw new Error("Workspace stackje.json changed during ingestion");
        let manifestInput;
        try {
            manifestInput = JSON.parse(new TextDecoder().decode(manifestBytes));
        }
        catch {
            throw new Error("Workspace stackje.json is not valid JSON");
        }
        const workspaceManifest = WorkspaceBundleManifestSchema.parse(manifestInput);
        // Only the inert registration artifact may have no routes; a design must publish `/`.
        if (workspaceManifest.routes["/"] !== "index.html")
            throw new Error("Bundle root route must target index.html");
        let files = await Promise.all(publicEntries.map(async (entry) => {
            const body = await workspace.readFile(`${root}/${entry.path}`);
            if (body.byteLength !== entry.size)
                throw new Error(`Workspace file changed during ingestion: ${entry.path}`);
            return { path: entry.path, body, contentType: inferredContentType(entry.path) };
        }));
        if (options.rewriteMarkers) {
            const html = Object.fromEntries(files.filter((file) => file.contentType.startsWith("text/html")).map((file) => [file.path, new TextDecoder().decode(file.body)]));
            const rewritten = options.rewriteMarkers(html, workspaceManifest);
            if (rewritten.unconverted.length) {
                const [first] = rewritten.unconverted;
                throw new Error(`Bundle content marker is not valid: ${first.marker} in ${first.file}: ${first.reason}${rewritten.unconverted.length > 1 ? ` (and ${rewritten.unconverted.length - 1} more)` : ""}`);
            }
            files = files.map((file) => html[file.path] !== undefined && rewritten.files[file.path] !== html[file.path] ? { ...file, body: new TextEncoder().encode(rewritten.files[file.path]) } : file);
        }
        return this.ingest(siteId, { files, routes: workspaceManifest.routes, bindings: workspaceManifest.bindings, nodes: workspaceManifest.nodes, repeats: workspaceManifest.repeats, itemPages: workspaceManifest.itemPages }, options);
    }
    async load(siteIdInput, artifactDigest) {
        const siteId = identifier.parse(siteIdInput);
        hexDigest.parse(artifactDigest);
        const stored = await this.objects.get(`${prefix(siteId, artifactDigest)}/manifest.json`);
        if (!stored || stored.contentType !== "application/json")
            throw new Error("Bundle manifest not found");
        const manifest = BundleManifestSchema.parse(JSON.parse(new TextDecoder().decode(stored.body)));
        const { digest: claimedDigest, ...unsigned } = manifest;
        if (manifest.siteId !== siteId || claimedDigest !== artifactDigest || await digest(unsigned) !== claimedDigest) {
            throw new Error("Bundle manifest integrity failure");
        }
        return manifest;
    }
    /** Creates an immutable artifact with one text binding redirected; the verified site bytes stay exact. */
    async rebindText(siteId, artifactDigest, bindingIdInput, recordId, field, nodeId) {
        const manifest = await this.load(siteId, artifactDigest);
        const binding = manifest.bindings.find((candidate) => candidate.id === bindingIdInput);
        if (!binding || binding.kind !== "text")
            throw new Error("Text binding does not exist");
        const files = await this.verifiedFiles(manifest);
        const bindings = manifest.bindings.map((candidate) => {
            if (candidate.id !== bindingIdInput)
                return candidate;
            if (candidate.kind !== "text")
                throw new Error("Text binding does not exist");
            return { ...candidate, recordId, field };
        });
        const nodes = manifest.nodes.map((node) => nodeId && node.id === nodeId && node.file === binding.file && !node.recordIds.includes(recordId)
            ? { ...node, recordIds: [...node.recordIds, recordId] }
            : node);
        return this.ingest(siteId, { files, routes: manifest.routes, bindings, nodes, repeats: manifest.repeats, itemPages: manifest.itemPages });
    }
    /**
     * Creates an immutable artifact where one unbound `<img>` shows a media record. The image is the
     * `occurrence`-th unbound `<img>` in `file` whose src is exactly `src`; only a binding marker is
     * added to its tag, every other byte stays as it was.
     */
    async bindImage(siteId, artifactDigest, file, src, occurrence, bindingIdInput, recordId, nodeId) {
        const manifest = await this.load(siteId, artifactDigest);
        if (!manifest.files[file]?.contentType.startsWith("text/html"))
            throw new Error("Page does not exist");
        const id = bindingId.parse(bindingIdInput);
        if (manifest.bindings.some((binding) => binding.id === id))
            throw new Error("Binding already exists");
        const files = await this.verifiedFiles(manifest);
        const page = files.find((candidate) => candidate.path === file);
        const html = new TextDecoder().decode(page.body);
        let seen = 0;
        let found = -1;
        for (const match of html.matchAll(/<img\b[^>]*>/gi)) {
            const tag = match[0];
            if (/\sdata-stackje-bind\s*=/i.test(tag))
                continue;
            const value = /\ssrc\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+))/i.exec(tag);
            if (decodeEntities(value?.[1] ?? value?.[2] ?? value?.[3] ?? "") !== src)
                continue;
            if (seen++ === occurrence) {
                found = match.index;
                break;
            }
        }
        if (found < 0)
            throw new Error("That photo is no longer on the page");
        const insertAt = found + "<img".length;
        page.body = new TextEncoder().encode(`${html.slice(0, insertAt)} data-stackje-bind="${id}"${html.slice(insertAt)}`);
        const bindings = [...manifest.bindings, { id, file, kind: "image", recordId }];
        const nodes = manifest.nodes.map((node) => nodeId && node.id === nodeId && node.file === file && !node.recordIds.includes(recordId)
            ? { ...node, recordIds: [...node.recordIds, recordId] }
            : node);
        return this.ingest(siteId, { files, routes: manifest.routes, bindings, nodes, repeats: manifest.repeats, itemPages: manifest.itemPages });
    }
    async verifiedFiles(manifest) {
        return Promise.all(Object.entries(manifest.files).map(async ([path, file]) => {
            const stored = await this.objects.get(`${prefix(manifest.siteId, manifest.digest)}/files/${path}`);
            if (!stored || stored.contentType !== file.contentType || stored.body.byteLength !== file.size || await hashBytes(stored.body) !== file.sha256) {
                throw new Error("Bundle artifact is incomplete");
            }
            return { path, body: stored.body, contentType: stored.contentType };
        }));
    }
    /** HTML source for deterministic design-order derivation. */
    async htmlBodies(siteId, artifactDigest, onlyFiles) {
        const manifest = await this.load(siteId, artifactDigest);
        const result = {};
        for (const [path, file] of Object.entries(manifest.files)) {
            if (!file.contentType.startsWith("text/html") || (onlyFiles && !onlyFiles.includes(path)))
                continue;
            const stored = await this.objects.get(`${prefix(siteId, artifactDigest)}/files/${path}`);
            if (!stored || await hashBytes(stored.body) !== file.sha256)
                throw new Error("Bundle artifact is incomplete");
            result[path] = new TextDecoder().decode(stored.body);
        }
        return result;
    }
    /**
     * Rehydrates a disposable design workspace from immutable, hash-verified bundle bytes. With
     * `convertMarkers`, the HTML is written with content markers in place of binding ids.
     */
    async restoreWorkspace(siteId, artifactDigest, workspace, outputDirectory, options = {}) {
        const manifest = await this.load(siteId, artifactDigest);
        const root = outputDirectory.replace(/\/$/, "");
        const loaded = [];
        for (const [path, file] of Object.entries(manifest.files)) {
            const stored = await this.objects.get(`${prefix(manifest.siteId, manifest.digest)}/files/${path}`);
            if (!stored || stored.contentType !== file.contentType || stored.body.byteLength !== file.size || await hashBytes(stored.body) !== file.sha256) {
                throw new Error("Bundle artifact is incomplete");
            }
            loaded.push({ path, body: stored.body, html: file.contentType.startsWith("text/html") });
        }
        if (options.convertMarkers) {
            const html = Object.fromEntries(loaded.filter((file) => file.html).map((file) => [file.path, new TextDecoder().decode(file.body)]));
            const converted = options.convertMarkers(html, manifest.bindings, manifest.repeats);
            for (const file of loaded)
                if (file.html && converted.files[file.path] !== html[file.path])
                    file.body = new TextEncoder().encode(converted.files[file.path]);
            if (converted.unconverted.length)
                options.unconverted?.(converted.unconverted);
        }
        for (const file of loaded)
            await workspace.writeFile(`${root}/${file.path}`, file.body);
        await workspace.writeFile(`${root}/stackje.json`, `${JSON.stringify({
            schemaVersion: 1,
            rendererVersion: "bundle-v1",
            routes: manifest.routes,
            bindings: manifest.bindings,
            nodes: manifest.nodes,
            ...(manifest.repeats?.length ? { repeats: manifest.repeats } : {}),
            ...(manifest.itemPages ? { itemPages: manifest.itemPages } : {}),
        }, null, 2)}\n`);
        return manifest;
    }
    async serve(siteId, artifactDigest, requestPath, options = {}) {
        const manifest = await this.load(siteId, artifactDigest);
        let pathname;
        try {
            pathname = decodeURIComponent(requestPath);
        }
        catch {
            return new Response("Not found", { status: 404 });
        }
        const normalized = typeof options === "string" ? { contentSecurityPolicy: options } : options;
        // A pattern route (`/journal/{slug}`) names a template for the platform to fill in; it is never a page by itself.
        const missing = isNotFoundPreviewPath(pathname);
        const routeFile = missing ? undefined : normalized.routeFile ?? (Object.hasOwn(manifest.routes, pathname) && !/[{}]/.test(pathname) ? manifest.routes[pathname] : undefined);
        const directPath = pathname.startsWith("/") ? pathname.slice(1) : pathname;
        const filePath = missing ? "404.html" : routeFile ?? (Object.hasOwn(manifest.files, directPath) ? directPath : "404.html");
        const file = manifest.files[filePath];
        const stored = file && await this.objects.get(`${prefix(siteId, artifactDigest)}/files/${filePath}`);
        if (!stored || stored.contentType !== file?.contentType || await hashBytes(stored.body) !== file.sha256) {
            return new Response("Bundle unavailable", { status: 503, headers: { "Cache-Control": "no-store" } });
        }
        const headers = new Headers({
            "Content-Type": stored.contentType,
            "Cache-Control": normalized.cacheControl ?? "no-store",
            "X-Content-Type-Options": "nosniff",
        });
        if (normalized.noIndex !== false)
            headers.set("X-Robots-Tag", "noindex");
        if (normalized.contentSecurityPolicy)
            headers.set("Content-Security-Policy", normalized.contentSecurityPolicy);
        return new Response(stored.body, { status: routeFile || filePath !== "404.html" ? 200 : 404, headers });
    }
    /** Copies the accepted bytes into the existing release namespace; it never rebuilds them. */
    async materializeRelease(siteId, artifactDigest, releaseIdInput, options = {}) {
        const releaseId = identifier.parse(releaseIdInput);
        const manifest = await this.load(siteId, artifactDigest);
        const inventory = { rendererVersion: "bundle-v1", artifactDigest, files: {}, routes: manifest.routes, assets: {} };
        for (const [path, file] of Object.entries(manifest.files)) {
            const stored = await this.objects.get(`${prefix(siteId, artifactDigest)}/files/${path}`);
            if (!stored || stored.contentType !== file.contentType || await hashBytes(stored.body) !== file.sha256)
                throw new Error("Bundle artifact is incomplete");
            await verifiedPut(this.objects, `releases/${siteId}/${releaseId}/${path}`, stored.body, file.contentType, file.sha256);
            inventory.files[path] = { sha256: file.sha256, contentType: file.contentType };
        }
        if (options.writeInventory !== false) {
            const serialized = canonicalJson(inventory);
            await verifiedPut(this.objects, `releases/${siteId}/${releaseId}/inventory.json`, serialized, "application/json", await hashText(serialized));
        }
        return inventory;
    }
}
/** Intended for a dedicated cookie-free preview Worker/origin, never the admin application's origin. */
export function createBundlePreviewHandler(options) {
    return async (request) => {
        if (request.method !== "GET")
            return new Response("Not found", { status: 404 });
        const target = await options.resolve(request);
        if (!target || !await options.authorize(request, target))
            return new Response("Not found", { status: 404, headers: { "Cache-Control": "no-store" } });
        try {
            return await options.bundles.serve(target.siteId, target.artifactDigest, target.path, options.contentSecurityPolicy ?? DEFAULT_BUNDLE_PREVIEW_CSP);
        }
        catch {
            return new Response("Not found", { status: 404, headers: { "Cache-Control": "no-store" } });
        }
    };
}
